import { useCallback, useEffect, useState } from 'react'
import { adminRequest } from './EshopCategories'
import { friendlyDateTime, orderStatus, scheduleStatus } from './orderLabels'

type Brief = {
  id: string; orderNumber: string; customerName: string; status: string; scheduleStatus: string | null
  paymentStatus: string; paymentMethod: string; deliveryMethod: string; totalCents: number
  createdAt: string; term: string | null; storeName: string | null; items: string[]
}
type Dashboard = {
  today: string
  counts: { newOrders: number; pendingSchedule: number; unpaidOnline: number; activeOrders: number; today: number; tomorrow: number }
  revenue: { last7: { orders: number; totalCents: number }; prev7: { orders: number; totalCents: number }; series: Array<{ day: string; orders: number; totalCents: number }> }
  todayOrders: Brief[]
  tomorrowOrders: Brief[]
  recentOrders: Brief[]
  catalog: { products: number; inEshop: number; issues: { noCategory: number; noAllergens: number; noVat: number; noPhoto: number } }
  stores: { active: number; issues: { noAddress: number; notLinked: number; genericName: number } }
}

export type DashboardTarget =
  | { page: 'Objednávky'; scheduleStatus?: string; status?: string; orderNumber?: string; preset?: 'today' | 'tomorrow' }
  | { page: 'Produkty'; issue?: 'noCategory' | 'noAllergens' | 'noVat' | 'noPhoto' }
  | { page: 'Prevádzky' | 'Kategórie' | 'Faktúry' }

const euro = (cents: number) => (cents / 100).toLocaleString('sk-SK', { style: 'currency', currency: 'EUR', maximumFractionDigits: cents % 100 ? 2 : 0 })
const plural = (n: number, one: string, few: string, many: string) => `${n} ${n === 1 ? one : n >= 2 && n <= 4 ? few : many}`
const time = (iso: string) => new Date(iso).toLocaleTimeString('sk-SK', { timeZone: 'Europe/Bratislava', hour: 'numeric', minute: '2-digit' })

export default function DashboardAdmin({ onNavigate }: { onNavigate: (target: DashboardTarget) => void }) {
  const [data, setData] = useState<Dashboard | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [day, setDay] = useState<'today' | 'tomorrow'>('today')

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try { setData(await adminRequest('/api/admin/dashboard')) }
    catch (e) { setError(e instanceof Error ? e.message : 'Prehľad sa nepodarilo načítať.') }
    finally { setLoading(false) }
  }, [])
  useEffect(() => {
    void load()
    const timer = window.setInterval(() => void load(), 60000)
    return () => window.clearInterval(timer)
  }, [load])

  if (!data) return <div className="panel db-loading">{error ? <><p className="error-message">{error}</p><button className="pa-ghost" onClick={() => void load()}>Skúsiť znova</button></> : <p className="empty-message">Načítavam prehľad…</p>}</div>

  const { counts, revenue, catalog, stores } = data
  const change = revenue.prev7.totalCents ? Math.round((revenue.last7.totalCents - revenue.prev7.totalCents) / revenue.prev7.totalCents * 100) : null
  const maxDay = Math.max(1, ...revenue.series.map(d => d.totalCents))
  const list = day === 'today' ? data.todayOrders : data.tomorrowOrders
  const dateLabel = new Date(`${data.today}T12:00:00Z`).toLocaleDateString('sk-SK', { timeZone: 'Europe/Bratislava', weekday: 'long', day: 'numeric', month: 'long' })

  const todos: Array<{ key: string; count: number; label: string; hint: string; tone: 'warn' | 'bad' | 'info'; target: DashboardTarget }> = [
    { key: 'schedule', count: counts.pendingSchedule, label: 'Potvrdiť termín', hint: 'zákazník čaká na potvrdenie termínu', tone: 'warn', target: { page: 'Objednávky', scheduleStatus: 'PENDING' } },
    { key: 'new', count: counts.newOrders, label: 'Nové objednávky', hint: 'ešte neboli potvrdené', tone: 'info', target: { page: 'Objednávky', status: 'NEW' } },
    { key: 'unpaid', count: counts.unpaidOnline, label: 'Nedokončené platby kartou', hint: 'platba čaká alebo zlyhala', tone: 'bad', target: { page: 'Objednávky' } },
    { key: 'cat', count: catalog.issues.noCategory, label: 'Produkty bez kategórie', hint: 'zákazníci ich v e-shope nevidia', tone: 'warn', target: { page: 'Produkty', issue: 'noCategory' } },
    { key: 'vat', count: catalog.issues.noVat, label: 'Produkty bez DPH', hint: 'nedajú sa zaplatiť kartou', tone: 'bad', target: { page: 'Produkty', issue: 'noVat' } },
    { key: 'all', count: catalog.issues.noAllergens, label: 'Produkty bez alergénov', hint: 'povinný údaj pre zákazníka', tone: 'warn', target: { page: 'Produkty', issue: 'noAllergens' } },
    { key: 'photo', count: catalog.issues.noPhoto, label: 'Produkty bez fotografie', hint: 'v e-shope pôsobia prázdno', tone: 'info', target: { page: 'Produkty', issue: 'noPhoto' } },
    { key: 'store', count: stores.issues.noAddress + stores.issues.genericName, label: 'Prevádzky bez adresy alebo názvu', hint: 'zákazník nevie, kam ísť', tone: 'warn', target: { page: 'Prevádzky' } },
    { key: 'pos', count: stores.issues.notLinked, label: 'Prevádzky bez pokladne', hint: 'objednávky nejdú do FiskalPRO', tone: 'info', target: { page: 'Prevádzky' } },
  ].filter(t => t.count > 0) as typeof todos

  return <div className="db">
    <div className="db-greeting">
      <div><h2>Dobrý deň</h2><p>{dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1)} · {plural(counts.activeOrders, 'rozpracovaná objednávka', 'rozpracované objednávky', 'rozpracovaných objednávok')}</p></div>
      <button type="button" className="pa-ghost" onClick={() => void load()} disabled={loading}>{loading ? 'Načítavam…' : '↻ Obnoviť'}</button>
    </div>
    {error && <p className="error-message">{error}</p>}

    <div className="db-kpis">
      <button type="button" className="db-kpi is-accent" onClick={() => onNavigate({ page: 'Objednávky', preset: 'today' })}>
        <span>Dnes na výdaj</span><strong>{counts.today}</strong><small>zajtra {counts.tomorrow}</small>
      </button>
      <button type="button" className={`db-kpi ${counts.pendingSchedule ? 'is-warn' : ''}`} onClick={() => onNavigate({ page: 'Objednávky', scheduleStatus: 'PENDING' })}>
        <span>Čaká na potvrdenie</span><strong>{counts.pendingSchedule}</strong><small>{counts.pendingSchedule ? 'termín treba potvrdiť' : 'všetko potvrdené'}</small>
      </button>
      <button type="button" className="db-kpi" onClick={() => onNavigate({ page: 'Objednávky', status: 'NEW' })}>
        <span>Nové objednávky</span><strong>{counts.newOrders}</strong><small>na kontrolu</small>
      </button>
      <div className="db-kpi is-static">
        <span>Tržby za 7 dní</span><strong>{euro(revenue.last7.totalCents)}</strong>
        <small>{plural(revenue.last7.orders, 'objednávka', 'objednávky', 'objednávok')}{change !== null && <b className={change >= 0 ? 'is-up' : 'is-down'}> {change >= 0 ? '▲' : '▼'} {Math.abs(change)} %</b>}</small>
      </div>
    </div>

    <div className="db-grid">
      <section className="panel db-card db-agenda">
        <header className="db-card-head">
          <h3>Výdaj objednávok</h3>
          <div className="pa-tabs">
            <button type="button" className={day === 'today' ? 'is-on' : ''} onClick={() => setDay('today')}>Dnes<span>{counts.today}</span></button>
            <button type="button" className={day === 'tomorrow' ? 'is-on' : ''} onClick={() => setDay('tomorrow')}>Zajtra<span>{counts.tomorrow}</span></button>
          </div>
        </header>
        {!list.length ? <div className="db-empty"><span>♡</span>{day === 'today' ? 'Dnes nie je naplánovaný žiadny výdaj.' : 'Na zajtra zatiaľ nie je nič naplánované.'}</div> :
          <ol className="db-timeline">{list.map(o => {
            const st = orderStatus(o.status)
            const sch = scheduleStatus(o.scheduleStatus)
            return <li key={o.id}>
              <button type="button" onClick={() => onNavigate({ page: 'Objednávky', orderNumber: o.orderNumber })}>
                <time>{o.term ? time(o.term) : '—'}</time>
                <div className="db-tl-body">
                  <div className="db-tl-top"><strong>{o.customerName}</strong><span className="db-tl-num">{o.orderNumber}</span></div>
                  <span className="db-tl-items">{o.items.join(', ')}</span>
                  <div className="db-tl-tags">
                    <span className={`or-pill tone-${st.tone}`}>{st.label}</span>
                    {o.scheduleStatus !== 'CONFIRMED' && <span className={`or-pill tone-${sch.tone}`}>{sch.label}</span>}
                    <span className="db-tl-where">{o.deliveryMethod === 'COURIER' ? '🚗 Na adresu' : o.deliveryMethod === 'SHIPPING' ? '📦 Kuriér' : `🏠 ${o.storeName ?? ''}`}</span>
                  </div>
                </div>
                <b>{euro(o.totalCents)}</b>
              </button>
            </li>
          })}</ol>}
      </section>

      <section className="panel db-card db-todo">
        <header className="db-card-head"><h3>Treba vyriešiť</h3>{todos.length > 0 && <span className="db-count">{todos.length}</span>}</header>
        {!todos.length ? <div className="db-empty is-ok"><span>✓</span>Všetko je v poriadku.</div> :
          <ul>{todos.map(t => <li key={t.key}>
            <button type="button" onClick={() => onNavigate(t.target)}>
              <span className={`db-todo-num tone-${t.tone}`}>{t.count}</span>
              <span className="db-todo-text"><strong>{t.label}</strong><small>{t.hint}</small></span>
              <span aria-hidden="true">›</span>
            </button>
          </li>)}</ul>}
      </section>
    </div>

    <div className="db-grid is-bottom">
      <section className="panel db-card db-chart-card">
        <header className="db-card-head"><h3>Objednávky za 14 dní</h3><span className="pa-muted">podľa dňa prijatia, bez zrušených</span></header>
        <div className="db-chart" role="img" aria-label="Tržby za posledných 14 dní">
          {revenue.series.map(d => {
            const date = new Date(`${d.day}T12:00:00Z`)
            const isToday = d.day === data.today
            return <div key={d.day} className={`db-bar ${isToday ? 'is-today' : ''}`} title={`${date.toLocaleDateString('sk-SK', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'numeric' })}: ${euro(d.totalCents)} · ${plural(d.orders, 'objednávka', 'objednávky', 'objednávok')}`}>
              <span className="db-bar-value">{d.orders || ''}</span>
              <span className="db-bar-fill" style={{ height: `${Math.max(d.totalCents ? 6 : 2, Math.round(d.totalCents / maxDay * 100))}%` }} />
              <span className="db-bar-label">{date.getUTCDate()}.</span>
            </div>
          })}
        </div>
      </section>

      <section className="panel db-card db-recent">
        <header className="db-card-head"><h3>Posledné objednávky</h3><button type="button" className="pa-link" onClick={() => onNavigate({ page: 'Objednávky' })}>Všetky →</button></header>
        {!data.recentOrders.length ? <div className="db-empty"><span>♡</span>Zatiaľ žiadne objednávky.</div> :
          <ul>{data.recentOrders.map(o => {
            const st = orderStatus(o.status)
            return <li key={o.id}><button type="button" onClick={() => onNavigate({ page: 'Objednávky', orderNumber: o.orderNumber })}>
              <span className="db-avatar" aria-hidden="true">{o.customerName.trim().charAt(0).toUpperCase() || '?'}</span>
              <span className="db-recent-text"><strong>{o.customerName}</strong><small>{o.orderNumber} · {friendlyDateTime(o.createdAt)}</small></span>
              <span className="db-recent-right"><b>{euro(o.totalCents)}</b><span className={`or-pill tone-${st.tone}`}>{st.label}</span></span>
            </button></li>
          })}</ul>}
      </section>
    </div>
  </div>
}
